import { Box, Skeleton, Typography } from '@mui/material';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';

type OverdueDeal = {
  id: string;
  dealName: string;
  owner: string;
  closeDate: string;
};

const getYesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const Alert = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = React.useState<OverdueDeal[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchOverdueDeals = async () => {
      try {
        setLoading(true);
        const data = await DealsService.getDealsListView({
          page: 1,
          limit: 10,
          sort: 'closeDate:asc',
          closeDateTo: getYesterdayISO(),
          'stageName!=': ['Closed Lost', 'Closed Won'],
        });

        const source = Array.isArray(data) ? data : data?.deals || data?.data || [];

        const mapped: OverdueDeal[] = source
          .filter((deal: any) => {
            const stage = String(deal?.stageName || deal?.dealStageName || deal?.stage || '')
              .trim()
              .toLowerCase();
            return stage !== 'closed lost' && stage !== 'closed won';
          })
          .map((deal: any, index: number) => ({
            id: deal?.id || deal?.dealId || `overdue-${index}`,
            dealName:
              (deal?.name || deal?.title || deal?.dealName || '').trim() ||
              `Untitled Deal ${index + 1}`,
            owner:
              (
                deal?.ownerName ||
                deal?.assignedToName ||
                deal?.createdBy ||
                deal?.owner ||
                ''
              ).trim() || 'N/A',
            closeDate:
              (
                deal?.closeDate ||
                deal?.velocityTargetDate ||
                deal?.targetCloseDate ||
                deal?.expectedCloseDate ||
                ''
              ).trim() || '',
          }));

        setDeals(mapped);
      } catch (err) {
        console.error('Error fetching overdue deals', err);
        setDeals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOverdueDeals();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '14px',
        border: `1px solid ${zeplinColor.Neutral5}`,
        height: { xs: 'calc(100vh - 490px)', xl: 'calc(100vh - 650px)' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `1px solid ${zeplinColor.Neutral5}`,
        }}
      >
        <Typography variant='responsiveHeading3'>
          Deal Alerts{!loading ? ` (${deals.length})` : ''}
        </Typography>
        <Typography
          variant='responsiveButtonSmall'
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/deals')}
        >
          See All
        </Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          height: { xs: 'calc(100vh - 490px)', xl: 'calc(100vh - 650px)' },
          overflowY: 'auto',
        }}
      >
        {loading ? (
          [1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{ padding: '0px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Skeleton variant='text' width='60%' height={20} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Skeleton variant='text' width='30%' height={16} />
                <Skeleton variant='text' width='30%' height={16} />
              </Box>
            </Box>
          ))
        ) : deals.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: { xs: 'calc(100vh - 560px)', xl: 'calc(100vh - 750px)' },
              py: '40px',
            }}
          >
            <img src='/assets/checkmark-green.png' width={48} height={48} alt='on track' />
            <Typography
              variant='responsiveHeading3'
              color={zeplinColor.BasicBlack}
              sx={{ marginTop: { xs: '22px', xl: '24px' } }}
            >
              Everything Is On Track
            </Typography>
            <Typography
              variant='responsiveBody3'
              color={zeplinColor.Neutral8}
              sx={{ width: '70%', textAlign: 'center', marginTop: { xs: '10px', xl: '12px' } }}
            >
              No stalled deals or urgent risks detected. Your pipeline is healthy and moving on
              schedule.
            </Typography>
          </Box>
        ) : (
          deals.map((deal, index) => (
            <Box
              key={deal.id}
              sx={{
                padding: '12px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                borderBottom:
                  index !== deals.length - 1 ? `1px solid ${zeplinColor.Neutral5}` : 'none',
                cursor: 'pointer',
                '&:hover': { backgroundColor: zeplinColor.Neutral2 },
              }}
              onClick={() => navigate(`/deals/${deal.id}`)}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Typography variant='responsiveHeading4'>{deal.dealName}</Typography>
                <Box
                  sx={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: zeplinColor.Error,
                    flexShrink: 0,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <img src='/assets/user-black.svg' alt='owner' />
                  <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                    {deal.owner}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img src='/assets/calendar.svg' alt='date' />
                  <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                    {deal.closeDate ? Utilities.formatDate(deal.closeDate) : 'N/A'}
                  </Typography>
                  <img src='/assets/overdue-icon.svg' alt='overdue' />
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default Alert;
